<template>
  <div class="grid grid-rows-[1fr_auto] h-full overflow-y-hidden md:overflow-y-auto w-full md:pt-0">
    <div class="flex flex-col w-full h-full overflow-y-hidden md:overflow-y-auto pt-2">
    <ConfirmDialog />
    <tabs-wrapper contentClass="md:px-8 md:pt-4" @tab-select="onTabSelect">
      <template #header-end-0>
        <Button
          label="מכירת כרטיסייה ללקוח"
          icon="pi pi-plus"
          size="small"
          @click="openSellBundlePack"
        />
      </template>
      <template #header-end-1>
        <Button
          label="מכירת שובר ללקוח"
          icon="pi pi-plus"
          size="small"
          @click="openNewVoucherDrawer"
        />
      </template>
      <template #header-end-2>
        <Button
          label="קופון חדש"
          icon="pi pi-plus"
          size="small"
          @click="openNewCoupon"
        />
      </template>

      <!-- ── Bundles tab ── -->
      <tab title="כרטיסיות" description="חבילות שירות מרובות מימושים — למכירה באתר העסק וניהול כרטיסיות פעילות">
        <div class="flex flex-col w-full h-full">
          <!-- Templates rail header -->
          <div class="flex items-baseline justify-between px-4 md:px-0 pt-3 pb-1">
            <div>
              <div class="text-sm font-semibold text-gray-900">חבילות שירות</div>
              <div class="text-[11px] text-gray-500">למכירה באתר העסק</div>
            </div>
            <span class="text-[11px] text-gray-400">{{ bundleTemplates.length }} פריטים</span>
          </div>

          <!-- Templates horizontal rail -->
          <div class="flex gap-2.5 px-4 md:px-0 pt-2 pb-1 flex-none overflow-x-auto hide-scrollbar">
            <bundle-template-card
              v-for="template of bundleTemplates"
              :key="template._id"
              :template="template"
              @click="openEditBundleTemplate(template)"
            />
            <new-template-card kind="bundles" @click="openNewBundleTemplate" />
          </div>

          <!-- Sold list header -->
          <div class="flex items-baseline justify-between px-4 md:px-0 pt-5 pb-1">
            <div class="text-sm font-semibold text-gray-900">כרטיסיות פעילות</div>
            <span class="text-[11px] text-gray-400">{{ activePacks.length }}</span>
          </div>

          <div v-if="loadingPacks" class="w-full flex justify-center items-center p-8">
            <loader />
          </div>
          <div v-else-if="issuedPacks.length === 0" class="w-full flex flex-col items-center justify-center py-8 text-gray-500">
            <i class="pi pi-id-card text-3xl mb-3 text-gray-300" />
            <span class="text-sm">עוד לא נמכרו כרטיסיות</span>
          </div>
          <div v-else class="flex flex-col">
            <issued-bundle-pack-row
              v-for="pack of issuedPacks"
              :key="pack._id"
              :pack="pack"
              @click="onIssuedPackClick(pack)"
            />
          </div>
        </div>
      </tab>

      <!-- ── Vouchers tab ── -->
      <tab title="שוברים" description="שוברי מתנה בסכום קבוע — מכירה ללקוח ומעקב אחר מימושים">
        <div class="flex flex-col w-full h-full">
          <!-- Templates rail header -->
          <div class="flex items-baseline justify-between px-4 md:px-0 pt-3 pb-1">
            <div>
              <div class="text-sm font-semibold text-gray-900">שוברי מתנה</div>
              <div class="text-[11px] text-gray-500">למכירה באתר העסק</div>
            </div>
            <span class="text-[11px] text-gray-400">{{ voucherTemplates.length }} פריטים</span>
          </div>

          <!-- Templates horizontal rail -->
          <div class="flex gap-2.5 px-4 md:px-0 pt-2 pb-1 flex-none overflow-x-auto hide-scrollbar">
            <voucher-template-card
              v-for="template of voucherTemplates"
              :key="template._id"
              :template="template"
              @click="openEditVoucherTemplate(template)"
            />
            <new-template-card kind="vouchers" @click="openNewVoucherTemplate" />
          </div>

          <!-- Sold list header -->
          <div class="flex items-baseline justify-between px-4 md:px-0 pt-5 pb-1">
            <div class="text-sm font-semibold text-gray-900">שוברים שנמכרו</div>
            <span class="text-[11px] text-gray-400">{{ allVouchers.length }}</span>
          </div>

          <div v-if="loadingVouchers" class="w-full flex justify-center items-center p-8">
            <loader />
          </div>
          <div v-else-if="allVouchers.length > 0" class="flex flex-col">
            <voucher-row
              v-for="voucher of sortedVouchers"
              :key="voucher._id"
              :voucher="voucher"
              @click="showVoucher(voucher)"
            />
          </div>
          <div v-else class="w-full flex flex-col items-center justify-center py-8 text-gray-500">
            <i class="pi pi-ticket text-3xl mb-3 text-gray-300" />
            <span class="text-sm">עוד לא נמכרו שוברים</span>
          </div>
        </div>
      </tab>

      <!-- ── Coupon codes tab ── -->
      <tab title="קודי קופון" description="קודי הנחה לקמפיינים — אחוז או סכום, עם תוקף ומגבלת שימוש">
        <div class="flex flex-col w-full h-full ">
          <!-- Intro header -->
          <div class="flex items-baseline justify-between px-4 md:px-0 pt-3 pb-1">
            <div>
              <div class="text-sm font-semibold text-gray-900">קודי קופון</div>
              <div class="text-[11px] text-gray-500">הנחה שהלקוחות מזינים בעת קביעת תור</div>
            </div>
            <span class="text-[11px] text-gray-400">{{ coupons.length }} קופונים</span>
          </div>

          <div v-if="loadingCoupons" class="w-full flex justify-center items-center p-8">
            <loader />
          </div>
          <div
            v-else-if="coupons.length > 0"
            class="flex flex-col md:flex-row md:flex-wrap w-full overflow-y-auto px-4 md:px-0 gap-2 md:gap-4 mt-2 mb-4"
          >
            <div
              v-wave
              v-for="coupon of coupons"
              :key="coupon._id"
              class="w-full md:w-[400px] border rounded-md cursor-pointer hover:bg-gray-50 p-3 bg-white"
              @click="openEditCoupon(coupon)"
            >
              <coupon-row :coupon="coupon" />
            </div>
          </div>
          <div v-else class="w-full flex flex-col items-center justify-center py-12 text-gray-500">
            <i class="pi pi-tag text-4xl text-gray-300" />
            <span class="text-base font-medium mt-4">עוד אין קופונים</span>
            <span class="text-center px-6 font-light mt-1 text-sm">צרו קוד קופון להנחה שהלקוחות יזינו בעת קביעת תור</span>
            <Button class="mt-4" label="יצירת קופון" icon="pi pi-plus" size="small" @click="openNewCoupon" />
          </div>
        </div>
      </tab>
    </tabs-wrapper>
    </div>

    <div v-if="isMobile && bottomCta" class="bg-white p-4 flex justify-center items-center shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.05)]">
      <Button
        v-wave
        :icon="bottomCta.icon"
        :label="bottomCta.label"
        class="w-full shadow-xl md:shadow-none"
        @click="bottomCta.action"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAdminStore } from '@app/stores';
import { AdminRouteNames } from '@app/router/route-names';
import {
  BundlePackStatus,
  IBundlePackTemplate,
  ICoupon,
  ICustomerBundlePackWithCustomer,
  IVoucher,
  IVoucherTemplate
} from '@torim-app/types';
import { BundlePacksApi } from '@app/api/admin/bundle-packs-api';
import { PromotionsApi } from '@app/api/admin/promotions-api';
import BundleTemplateCard from '@app/components/admin/components/BundleTemplateCard.vue';
import VoucherTemplateCard from '@app/components/admin/components/VoucherTemplateCard.vue';
import NewTemplateCard from '@app/components/admin/components/NewTemplateCard.vue';
import IssuedBundlePackRow from '@app/components/admin/components/IssuedBundlePackRow.vue';
import IssuedBundlePackDrawer from '@app/components/admin/components/IssuedBundlePackDrawer.vue';
import BundlePackTemplateDrawer from '@app/components/admin/components/BundlePackTemplateDrawer.vue';
import NewBundlePackTemplate from '@app/components/admin/modals/NewBundlePackTemplate.vue';
import VoucherTemplateDrawer from '@app/components/admin/modals/VoucherTemplateDrawer.vue';
import SellBundlePackDrawer from '@app/components/admin/modals/SellBundlePackDrawer.vue';
import VoucherRow from '@app/components/admin/components/VoucherRow.vue';
import VoucherDrawer from '@app/components/admin/components/VoucherDrawer.vue';
import NewVoucher from '@app/components/admin/modals/NewVoucher.vue';
import CouponRow from '@app/components/admin/components/CouponRow.vue';
import CouponDrawer from '@app/components/admin/modals/CouponDrawer.vue';
import Tab from './components/admin-tabs/Tab.vue';
import { useDrawer } from '@torim-app/shared-components';
import { useResponsive } from '@app/composables/responsive';
import { trackEvent } from '@app/composables/posthog';
import ConfirmDialog from 'primevue/confirmdialog';

const adminStore = useAdminStore();
const { setPage } = adminStore;
const { openDrawer, closeDrawer } = useDrawer();
const { isMobile } = useResponsive();
const route = useRoute();

const bundleTemplates = ref<IBundlePackTemplate[]>([]);
const voucherTemplates = ref<IVoucherTemplate[]>([]);
const allPacks = ref<ICustomerBundlePackWithCustomer[]>([]);
const loadingPacks = ref(false);
const allVouchers = ref<Array<IVoucher & { isPaid: boolean }>>([]);
const loadingVouchers = ref(false);
const coupons = ref<ICoupon[]>([]);
const loadingCoupons = ref(false);

// Genuinely active packs — used for the "כרטיסיות פעילות" header counter.
const activePacks = computed(() =>
  allPacks.value.filter(p => p.status === BundlePackStatus.Active)
);

// All issued (non-cancelled) packs — the list shows these with their status chips.
const issuedPacks = computed(() =>
  allPacks.value.filter(p => p.status !== BundlePackStatus.Cancelled)
);

// Newest first — ObjectId _id is monotonic with creation time, so it orders
// vouchers new→old without a dedicated createdAt field.
const byNewest = (a: { _id?: string }, b: { _id?: string }) => (b._id ?? '').localeCompare(a._id ?? '');
const sortedVouchers = computed(() => [
  ...allVouchers.value.filter(x => !x.used).sort(byNewest),
  ...allVouchers.value.filter(x => x.used).sort(byNewest)
]);

const activeTabIndex = ref<number>(route.query.tab !== undefined ? Number(route.query.tab) : 0);

watch(() => route.query.tab, (t) => {
  if (t !== undefined) activeTabIndex.value = Number(t);
});

const onTabSelect = (idx: number) => {
  activeTabIndex.value = idx;
};

const bottomCta = computed(() => {
  if (activeTabIndex.value === 0)
    return { icon: 'pi pi-plus', label: 'מכירת כרטיסייה ללקוח', action: openSellBundlePack };
  if (activeTabIndex.value === 1)
    return { icon: 'pi pi-plus', label: 'מכירת שובר ללקוח', action: openNewVoucherDrawer };
  if (activeTabIndex.value === 2)
    return { icon: 'pi pi-plus', label: 'קופון חדש', action: openNewCoupon };
  return null;
});

// ── Fetchers ──
const fetchBundleTemplates = async () => {
  try {
    bundleTemplates.value = await BundlePacksApi.fetchTemplates();
  } catch (e) {
    console.error(e);
  }
};

const fetchVoucherTemplates = async () => {
  try {
    voucherTemplates.value = await PromotionsApi.fetchVoucherTemplates();
  } catch (e) {
    console.error(e);
  }
};

const fetchAllPacks = async () => {
  loadingPacks.value = true;
  try {
    allPacks.value = await BundlePacksApi.fetchAllPacks();
  } catch (e) {
    console.error(e);
  } finally {
    loadingPacks.value = false;
  }
};

const fetchVouchers = async () => {
  loadingVouchers.value = true;
  try {
    allVouchers.value = await PromotionsApi.fetchAllVouchers();
  } catch (e) {
    console.error(e);
  } finally {
    loadingVouchers.value = false;
  }
};

const fetchCoupons = async () => {
  loadingCoupons.value = true;
  try {
    coupons.value = await PromotionsApi.fetchCoupons();
  } catch (e) {
    console.error(e);
  } finally {
    loadingCoupons.value = false;
  }
};

// ── Coupon actions ──
const openNewCoupon = () => {
  trackEvent('coupon_create_clicked');
  openDrawer(CouponDrawer, {
    coupon: null,
    onCreated: async () => { await fetchCoupons(); },
    onClose: closeDrawer
  }, { title: 'קופון חדש' });
};

const openEditCoupon = (coupon: ICoupon) => {
  trackEvent('coupon_edit_clicked');
  openDrawer(CouponDrawer, {
    coupon,
    onUpdated: async () => { await fetchCoupons(); },
    onDeleted: async () => { await fetchCoupons(); },
    onClose: closeDrawer
  }, { title: 'עריכת קופון', closable: true });
};

// ── Voucher (issued) actions ──
const showVoucher = (voucher: IVoucher & { isPaid: boolean }) => {
  trackEvent('voucher_details_viewed');
  openDrawer(VoucherDrawer, {
    selectedVoucher: voucher,
    onFetchVouchers: fetchVouchers
  }, { full: true, title: 'פרטי שובר', closable: true });
};

const openNewVoucherDrawer = () => {
  trackEvent('voucher_sell_to_customer_clicked');
  openDrawer(NewVoucher, {
    onCreatedVoucher: async (newVoucherId: string) => {
      closeDrawer();
      await fetchVouchers();
      const created = allVouchers.value.find(x => x._id === newVoucherId);
      if (created) showVoucher(created);
    },
    onClose: closeDrawer
  }, { title: 'מכירת שובר ללקוח' });
};

// ── Voucher templates (catalog) ──
const openNewVoucherTemplate = () => {
  trackEvent('voucher_template_create_clicked');
  openDrawer(VoucherTemplateDrawer, {
    template: null,
    onCreated: async () => { await fetchVoucherTemplates(); },
    onClose: closeDrawer
  }, { title: 'הוספת שובר לאתר' });
};

const openEditVoucherTemplate = (template: IVoucherTemplate) => {
  trackEvent('voucher_template_edit_clicked');
  openDrawer(VoucherTemplateDrawer, {
    template,
    onUpdated: async () => { await fetchVoucherTemplates(); },
    onDeleted: async () => { await fetchVoucherTemplates(); },
    onClose: closeDrawer
  }, { title: 'עריכת שובר באתר', closable: true });
};

// ── Bundle templates (catalog) ──
const openNewBundleTemplate = () => {
  trackEvent('bundle_pack_template_create_clicked');
  openDrawer(NewBundlePackTemplate, {
    onCreated: async () => { await fetchBundleTemplates(); },
    onClose: closeDrawer
  }, { title: 'חבילה חדשה לאתר' });
};

const openEditBundleTemplate = (template: IBundlePackTemplate) => {
  trackEvent('bundle_pack_template_details_viewed');
  openDrawer(BundlePackTemplateDrawer, {
    template,
    onUpdated: async () => { await fetchBundleTemplates(); },
    onDeleted: async () => { await fetchBundleTemplates(); }
  }, { title: 'עריכת חבילה', closable: true });
};

// ── Sell bundle pack to customer ──
const openSellBundlePack = () => {
  trackEvent('bundle_pack_sell_to_customer_clicked');
  openDrawer(SellBundlePackDrawer, {
    onAssigned: async () => { await fetchAllPacks(); },
    onClose: closeDrawer
  }, { title: 'מכירת כרטיסייה ללקוח', closable: true });
};

const onIssuedPackClick = (pack: ICustomerBundlePackWithCustomer) => {
  trackEvent('issued_bundle_pack_details_viewed');
  openDrawer(
    IssuedBundlePackDrawer,
    {
      pack,
      onChanged: async () => { await fetchAllPacks(); },
      onClose: closeDrawer
    },
    { title: 'פרטי כרטיסייה', closable: true }
  );
};

onMounted(() => {
  trackEvent('vouchers_viewed');
  setPage(AdminRouteNames.Vouchers);
  fetchVouchers();
  fetchVoucherTemplates();
  fetchBundleTemplates();
  fetchAllPacks();
  fetchCoupons();
});
</script>

<style scoped>
.hide-scrollbar::-webkit-scrollbar { display: none; }
.hide-scrollbar { scrollbar-width: none; }
</style>
